import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  InspectionDetails,
  InvoiceStatus,
  Money,
  PredefinedItem,
  Qty,
  WorkOrderAggregate,
  WorkOrderLaborLine,
  WorkOrderPartLine,
  WorkOrderSubletLine,
} from "@autorepair/contracts";
import { customersApi, predefinedItemsApi, shopProfileApi, techniciansApi, tenantSettingsApi, vehiclesApi, workOrdersApi, ApiError } from "../lib/api";
import {
  formatDate,
  formatDateTime,
  formatInvoiceNumber,
  formatMoney,
  formatQty,
  formatWorkOrderNumber,
  formatWorkOrderTaxRateLabel,
  parseMoneyInput,
  parseQtyInput,
} from "../lib/money";
import { applyDigitPreservingTransform } from "../lib/inputCursor";
import { useConfirmGate } from "../lib/useConfirm";
import { EmailDocumentDialog } from "../components/EmailDocumentDialog";
import { EmailSetupHint } from "../components/EmailSetupHint";
import { useShopType, useShopTypeLabels } from "../lib/theme";
import { EditPartLineModal } from "../components/EditPartLineModal";
import { EditLaborLineModal } from "../components/EditLaborLineModal";
import { EditSubletLineModal } from "../components/EditSubletLineModal";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernCheckboxRow,
  ModernConfirmButton,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInfoTip,
  ModernInput,
  ModernModal,
  ModernSelect,
  ModernStageTracker,
  ModernStatusPill,
  ModernTextarea,
  ModernTintButton,
  DownloadIcon,
  EmailIcon,
  InvoiceIcon,
  PrintIcon,
} from "../components/ui-modern";

/**
 * The modern theme's Work Order Editor — see docs/FRONTEND_BACKLOG.md Round
 * 60 and `Bay2Bill Redesign.dc.html` lines ~259-392 (the "isWorkOrder"
 * block). This is the single biggest/most business-logic-heavy screen in
 * the app (~1150 lines in Classic — mutations and JSX fully interleaved),
 * so EVERY mutation below is a deliberate line-for-line duplicate of
 * `WorkOrderEditorPageClassic.tsx`'s own (same queryKeys/mutationFns/
 * onSuccess/onError, including `applyResult()`/`handleError()` verbatim) —
 * this is not the place to improvise a "simpler" version; Round 57/58 both
 * exist because an earlier version of a page like this one DIDN'T keep the
 * work-orders list/customer history in sync after a mutation and had a
 * real, owner-reported stale-UI bug.
 *
 * Layout differs from Classic's single stacked column to match the mockup's
 * 2-column layout (sticky right rail on wide viewports), and some of
 * Classic's own separate cards (Customer, Vehicle) are folded into the
 * header instead of kept as their own cards — but every interactive
 * element/gate Classic has (every mutation, every confirm gate, every
 * canVoid/canUnvoid/isLocked/busy/hasBillableValue condition, the
 * Shop Supplies override row, the estimate preview iframe, the invoices
 * list) is reproduced here too, just restyled. Two honest layout calls,
 * same "restyle what's real, don't fabricate" rule every other screen in
 * this round followed:
 *  1. The mockup's flat "+ Part"/"+ Labor"/"+ Sublet" header buttons have no
 *     real interaction behind them in the mockup (it's a static demo) —
 *     Classic's REAL interaction is an always-visible inline add-form under
 *     each section's line list, so that's what's reproduced here instead of
 *     inventing a toggle/modal that doesn't exist in Classic.
 *  2. The mockup's "Vehicle history" rail card has no backing query on this
 *     page (Classic never fetches other work orders for this vehicle) —
 *     replaced with Classic's own real "Invoices" card (this work order's
 *     own issued invoices) rather than fabricating vehicle history.
 */

// Same actions/logic as WorkOrderEditorPageClassic.tsx's own NEXT_STEP, but
// "job" -> "work order" throughout — the owner asked for consistent
// terminology in the modern theme (2026-09-13); Classic keeps its own
// existing wording unchanged, per this whole effort's "never touch
// Classic" rule.
const NEXT_STEP: Record<string, { label: string; action: "approve" | "start" | "complete"; confirm: string } | undefined> = {
  estimate: { label: "Approve This Work Order", action: "approve", confirm: "Mark this work order as approved by the customer?" },
  approved: { label: "Start the Work", action: "start", confirm: "Mark this work order as started?" },
  in_progress: { label: "Mark Work Order Complete", action: "complete", confirm: "Mark this work order as complete?" },
};

/** Same order/labels as WorkOrderEditorPageClassic.tsx's own STAGE_ORDER/
 * STAGE_LABELS — duplicated (not imported) since Classic doesn't export
 * them and this round's rule is "never touch Classic's own file". Feeds
 * `ModernStageTracker` (see ui-modern.tsx) instead of Classic's own
 * private `StageTracker`. */
const STAGE_ORDER = ["estimate", "approved", "in_progress", "complete"] as const;
const STAGE_LABELS: Record<(typeof STAGE_ORDER)[number], string> = {
  estimate: "Estimate",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Complete",
};

const STATUS_HELP =
  "The left badge is work order progress; the right badge(s) are billing status — a work order becomes billed the moment it's invoiced, so its progress badge shows \"Completed\" once that happens rather than freezing on \"Invoiced.\" The billing badge shows whether it's been invoiced at all, and if so, its payment status (Not Paid, Partially Paid, Paid in Full, or Void if the invoice itself was voided). Check the invoice itself (open it from the Invoices card) for the full detail.";

const SUBLET_HELP =
  "Sublet is work done by an outside shop or vendor (e.g. towing, alignment, machine-shop work) that you bill to the customer through this work order, rather than doing in-house.";

/** A same-origin `<a download>` click, not `window.location.assign` — the
 * server's `Content-Disposition: attachment` header makes either work, but
 * a real anchor click never risks a full-page navigation if that header
 * were ever missing. */
function triggerDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function WorkOrderEditorPageModern() {
  const { workOrderNumber: workOrderNumberParam } = useParams();
  const workOrderNumber = Number(workOrderNumberParam);
  const navigate = useNavigate();
  // Set by whoever linked here (e.g. a customer's own work-order history)
  // so deleting can return to that same place instead of always dumping
  // back to the work orders list — see docs/FRONTEND_BACKLOG.md.
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? "/work-orders";
  // Same signal, reused for the breadcrumb below: arriving via a
  // customer's own work-order history (see CustomerDetailPage's Link)
  // means the real parent chain is Customers > [name] > Work Order, not
  // the flat Work Orders list. Mirrors InvoiceDetailPage's identical fix.
  const cameFromCustomer = returnTo.startsWith("/customers/");
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [editingPartLine, setEditingPartLine] = useState<WorkOrderPartLine | null>(null);
  const [editingLaborLine, setEditingLaborLine] = useState<WorkOrderLaborLine | null>(null);
  const [editingSubletLine, setEditingSubletLine] = useState<WorkOrderSubletLine | null>(null);
  const lifecycleConfirm = useConfirmGate();
  const invoiceConfirm = useConfirmGate();
  const unvoidConfirm = useConfirmGate();
  const jobDetailsConfirm = useConfirmGate();
  const notesConfirm = useConfirmGate();

  const woQuery = useQuery({
    queryKey: ["work-order", "by-number", workOrderNumber],
    queryFn: () => workOrdersApi.getByNumber(workOrderNumber),
  });
  const aggregate = woQuery.data?.aggregate;
  // The work order's OWN internal id — only known once the aggregate has
  // loaded (the URL now carries workOrderNumber, not this). Every mutation
  // below is only ever fired from JSX gated behind the loading guard
  // further down, so by the time any of them actually runs, `aggregate`
  // (and therefore `id`) is guaranteed to be real — same "trust it's
  // loaded by the time this executes" convention already used for
  // `aggregate!.workOrder.customerId` etc. just below.
  const id = aggregate?.workOrder.id as number;

  const customerQuery = useQuery({
    queryKey: ["customer", aggregate?.workOrder.customerId],
    queryFn: () => customersApi.get(aggregate!.workOrder.customerId),
    enabled: !!aggregate,
  });
  const vehicleQuery = useQuery({
    queryKey: ["vehicle", aggregate?.workOrder.vehicleId],
    queryFn: () => vehiclesApi.get(aggregate!.workOrder.vehicleId),
    enabled: !!aggregate,
  });
  const techniciansQuery = useQuery({ queryKey: ["technicians"], queryFn: () => techniciansApi.list({ limit: 100 }) });
  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const shopSuppliesEnabled = tenantSettingsQuery.data?.settings.shopSuppliesEnabled ?? true;
  const shopTypeLabels = useShopTypeLabels();
  const hasVehicles = useShopType() !== "detailing";

  function applyResult(result: { aggregate: WorkOrderAggregate }): void {
    queryClient.setQueryData(["work-order", "by-number", workOrderNumber], result);
    // Every mutation that lands here (status changes, line-item edits,
    // technician assignment, void/invoice) changes something the Work
    // Orders list and a customer's own Work Order History both display
    // (status, total, invoice status) — without this, either screen kept
    // showing stale data until its 10s staleTime happened to expire on its
    // own. Matches ["work-orders"] by prefix, covering both
    // ["work-orders", "list", ...] and ["work-orders", "byCustomer", ...].
    void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
  }

  function handleError(err: unknown): void {
    if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object" && "aggregate" in err.body) {
      applyResult({ aggregate: (err.body as { aggregate: WorkOrderAggregate }).aggregate });
      setNotice("Someone else just updated this work order — showing the latest version. Please try again.");
    } else if (err instanceof Error) {
      setNotice(err.message);
    } else {
      setNotice("Something went wrong. Please try again.");
    }
  }

  const lifecycleMutation = useMutation({
    mutationFn: (action: "approve" | "start" | "complete") => {
      const version = aggregate!.workOrder.version;
      if (action === "approve") return workOrdersApi.approve(id, version);
      if (action === "start") return workOrdersApi.start(id, version);
      return workOrdersApi.complete(id, version);
    },
    onSuccess: applyResult,
    onError: handleError,
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => workOrdersApi.void(id, aggregate!.workOrder.version, reason),
    onSuccess: applyResult,
    onError: handleError,
  });

  const unvoidMutation = useMutation({
    mutationFn: () => workOrdersApi.unvoid(id, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  // Backs the "Print Estimate" button — a hidden iframe, not mounted with a
  // src until clicked, loaded and then print()'d via contentWindow once
  // ready (same pattern as InvoiceDetailPage's copy-print buttons). `token`
  // is a cache-buster: printing twice in a row must still change the src,
  // or the iframe never re-fires onLoad and print() never runs the second
  // time.
  const estimatePrintFrameRef = useRef<HTMLIFrameElement>(null);
  const [estimatePrintToken, setEstimatePrintToken] = useState<number | null>(null);
  const [printingEstimate, setPrintingEstimate] = useState(false);
  // Ready the moment an estimate record exists (and the job isn't void) —
  // there's no separate "has this been rendered yet" state to track
  // anymore: the estimate PDF is rendered fresh from the work order's
  // CURRENT lines on every request (see EstimateRenderingService,
  // docs/TRAPS.md), never a stored file that could be "not ready yet".
  // Voided is still excluded even if an estimate was printed before the
  // void — a voided job's estimate isn't meant to be handed to a customer
  // (un-void to view/print it again).
  const estimateDocumentReady = !!aggregate?.estimate && aggregate.workOrder.status !== "void";

  // Emailing the estimate needs BOTH a customer email and a configured shop
  // email (the shop's is the Reply-To) — the buttons hide otherwise.
  const shopProfileQuery = useQuery({ queryKey: ["shop-profile"], queryFn: shopProfileApi.get });
  const customerEmail = customerQuery.data?.customer.email?.trim() || null;
  const canEmailEstimate = !!customerEmail && !!shopProfileQuery.data?.shopProfile.email;
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const estimateEmailsQuery = useQuery({
    queryKey: ["document-emails", "estimate", id],
    queryFn: () => workOrdersApi.estimateEmailSummary(id),
    enabled: !!aggregate,
  });

  /** Issues the estimate record (allocating its number) if one doesn't
   * exist yet — see docs/FRONTEND_BACKLOG.md Round 3. Nothing left to
   * "render/store" afterward: the printable estimate is always the work
   * order's current state, rendered on demand. Called implicitly by
   * `requestEstimatePrint`/`requestEstimateDownload` below rather than its
   * own separate "Prepare" button/step — the owner asked for Print/Download
   * to just work the moment there's a billable line, not gated behind an
   * extra click first. */
  const prepareEstimateMutation = useMutation({
    mutationFn: () => workOrdersApi.issueEstimate(id, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  /** Opens the email dialog, issuing the estimate first if it doesn't exist yet
   * (same as Print/Download) so the preview shows the real estimate number. */
  function requestEstimateEmail(): void {
    if (estimateDocumentReady) {
      setEmailDialogOpen(true);
      return;
    }
    prepareEstimateMutation.mutate(undefined, { onSuccess: () => setEmailDialogOpen(true) });
  }

  function requestEstimatePrint(): void {
    if (estimateDocumentReady) {
      setPrintingEstimate(true);
      setEstimatePrintToken(Date.now());
      return;
    }
    prepareEstimateMutation.mutate(undefined, {
      onSuccess: () => {
        setPrintingEstimate(true);
        setEstimatePrintToken(Date.now());
      },
    });
  }

  function requestEstimateDownload(): void {
    if (estimateDocumentReady) {
      triggerDownload(workOrdersApi.estimateDocumentUrl(id, true));
      return;
    }
    prepareEstimateMutation.mutate(undefined, {
      onSuccess: () => triggerDownload(workOrdersApi.estimateDocumentUrl(id, true)),
    });
  }

  const deleteMutation = useMutation({
    mutationFn: () => workOrdersApi.delete(id, aggregate!.workOrder.version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      navigate(returnTo);
    },
    onError: handleError,
  });

  const invoiceMutation = useMutation({
    mutationFn: () => workOrdersApi.issueInvoice(id, aggregate!.workOrder.version),
    onSuccess: (result) => {
      applyResult(result);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      const invoice = result.aggregate.invoices[result.aggregate.invoices.length - 1];
      if (invoice) navigate(`/invoices/${invoice.invoiceNumber}`, { state: { from: `/work-orders/${workOrderNumber}` } });
    },
    onError: handleError,
  });

  const deletePartLineMutation = useMutation({
    mutationFn: (lineId: number) => workOrdersApi.deletePartLine(id, lineId, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  const updatePartLineMutation = useMutation({
    mutationFn: (values: {
      lineId: number;
      partNumber: string;
      description: string;
      quantity: Qty;
      unitListPrice: Money;
      includeInShopSupplies: boolean;
      isTaxable?: boolean;
      inspection?: InspectionDetails;
    }) =>
      workOrdersApi.updatePartLine(id, values.lineId, {
        version: aggregate!.workOrder.version,
        partNumber: values.partNumber,
        description: values.description,
        quantity: values.quantity,
        unitListPrice: values.unitListPrice,
        includeInShopSupplies: values.includeInShopSupplies,
        isTaxable: values.isTaxable,
        ...(values.inspection ? { inspection: values.inspection } : {}),
      }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const deleteLaborLineMutation = useMutation({
    mutationFn: (lineId: number) => workOrdersApi.deleteLaborLine(id, lineId, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  const updateLaborLineMutation = useMutation({
    mutationFn: (values: { lineId: number; description: string; extendedAmount: Money; isTaxable?: boolean }) =>
      workOrdersApi.updateLaborLine(id, values.lineId, {
        version: aggregate!.workOrder.version,
        description: values.description,
        extendedAmount: values.extendedAmount,
        isTaxable: values.isTaxable,
      }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const shopSuppliesMutation = useMutation({
    mutationFn: (overrideAmount: Money | null) =>
      workOrdersApi.update(id, { version: aggregate!.workOrder.version, shopSuppliesOverrideAmount: overrideAmount }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const discountMutation = useMutation({
    mutationFn: (values: { discountType: "percent" | "amount" | null; discountValue: Money | null }) =>
      workOrdersApi.update(id, { version: aggregate!.workOrder.version, ...values }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const jobDetailsMutation = useMutation({
    mutationFn: (values: { customerConcern: string; odometerIn: number | null; odometerOut: number | null; flexField1: string | null }) =>
      workOrdersApi.update(id, { version: aggregate!.workOrder.version, ...values }),
    onSuccess: applyResult,
    onError: handleError,
  });

  // Customer-visible note prints on the invoice/estimate, so it still goes
  // through the generic, invoiced-locked `update()` — same rule as every
  // other field. Internal note has its OWN mutation, hitting the dedicated
  // endpoint that skips that lock (see UpdateInternalNotesRequest's own
  // comment) — the owner asked for it to stay editable even after
  // invoicing, since it's shop-only and never printed.
  const customerNotesMutation = useMutation({
    mutationFn: (customerNotes: string | null) => workOrdersApi.update(id, { version: aggregate!.workOrder.version, customerNotes }),
    onSuccess: applyResult,
    onError: handleError,
  });
  const internalNotesMutation = useMutation({
    mutationFn: (internalNotes: string | null) => workOrdersApi.updateInternalNotes(id, { version: aggregate!.workOrder.version, internalNotes }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const deleteSubletLineMutation = useMutation({
    mutationFn: (lineId: number) => workOrdersApi.deleteSubletLine(id, lineId, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  const updateSubletLineMutation = useMutation({
    mutationFn: (values: { lineId: number; description: string; vendorName: string | null; priceAmount: Money; isTaxable?: boolean }) =>
      workOrdersApi.updateSubletLine(id, values.lineId, {
        version: aggregate!.workOrder.version,
        description: values.description,
        vendorName: values.vendorName,
        priceAmount: values.priceAmount,
        isTaxable: values.isTaxable,
      }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const assignTechMutation = useMutation({
    mutationFn: (technicianId: number) => workOrdersApi.assignTechnician(id, aggregate!.workOrder.version, technicianId),
    onSuccess: applyResult,
    onError: handleError,
  });

  const unassignTechMutation = useMutation({
    mutationFn: (technicianId: number) => workOrdersApi.unassignTechnician(id, technicianId, aggregate!.workOrder.version),
    onSuccess: applyResult,
    onError: handleError,
  });

  // Add-line forms are modals (matching the mockup's compact "Job lines"
  // card header — see docs/FRONTEND_BACKLOG.md), not the always-visible
  // inline forms Classic keeps — so each mutation now takes its values as
  // an argument instead of closing over page-level input state, and the
  // modal component itself (defined at the bottom of this file, Modern-
  // only — Classic has no equivalent) owns that state/validation/confirm
  // gate, same self-contained pattern as EditPartLineModal etc.
  const [addingPart, setAddingPart] = useState(false);
  const addPartMutation = useMutation({
    mutationFn: (values: {
      partNumber: string;
      description: string;
      quantity: Qty;
      unitListPrice: Money | undefined;
      includeInShopSupplies: boolean;
      isTaxable?: boolean;
      isStateInspection?: boolean;
    }) => workOrdersApi.addPartLine(id, { version: aggregate!.workOrder.version, ...values }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const [addingLabor, setAddingLabor] = useState(false);
  const addLaborMutation = useMutation({
    // isTaxable omitted (undefined) unless the modal's own "Tax exempt"
    // checkbox was checked — the API then falls back to its own default
    // (the shop's laborTaxRate > 0). See WorkOrdersService.addLaborLine.
    mutationFn: (values: { description: string; extendedAmount: Money; isTaxable?: boolean }) =>
      workOrdersApi.addLaborLine(id, { version: aggregate!.workOrder.version, ...values }),
    onSuccess: applyResult,
    onError: handleError,
  });

  const [addingSublet, setAddingSublet] = useState(false);
  const addSubletMutation = useMutation({
    // See addLaborMutation's own comment — same shape.
    mutationFn: (values: { description: string; vendorName: string | undefined; priceAmount: Money; isTaxable?: boolean }) =>
      workOrdersApi.addSubletLine(id, { version: aggregate!.workOrder.version, ...values }),
    onSuccess: applyResult,
    onError: handleError,
  });

  // "+ Predefined Items" popup. Picking an item adds its line straight away; one that
  // can't be added as-is (a labor/sublet with no price, a part with no title) opens the
  // matching Add modal prefilled instead, so the missing value can be typed in.
  const [pickingPredefined, setPickingPredefined] = useState(false);
  const [prefillItem, setPrefillItem] = useState<PredefinedItem | null>(null);
  async function addFromPredefined(item: PredefinedItem): Promise<void> {
    const wo = aggregate!.workOrder;
    if (item.type === "part") {
      const values = partValuesFromItem(item, wo.partsTaxRateSnapshot);
      if (values) await addPartMutation.mutateAsync(values);
      else {
        setPrefillItem(item);
        setAddingPart(true);
      }
    } else if (item.type === "labor") {
      const values = laborValuesFromItem(item, wo.laborTaxRateSnapshot);
      if (values) await addLaborMutation.mutateAsync(values);
      else {
        setPrefillItem(item);
        setAddingLabor(true);
      }
    } else {
      const values = subletValuesFromItem(item, wo.subletTaxRateSnapshot);
      if (values) await addSubletMutation.mutateAsync(values);
      else {
        setPrefillItem(item);
        setAddingSublet(true);
      }
    }
    setPickingPredefined(false);
  }

  const [technicianToAdd, setTechnicianToAdd] = useState("");
  const assignConfirm = useConfirmGate();

  const [concern, setConcern] = useState<string | null>(null);
  const [odoIn, setOdoIn] = useState<string | null>(null);
  const [odoOut, setOdoOut] = useState<string | null>(null);
  const [flexField1, setFlexField1] = useState<string | null>(null);
  const [internalNotesDraft, setInternalNotesDraft] = useState<string | null>(null);
  const [customerNotesDraft, setCustomerNotesDraft] = useState<string | null>(null);

  if (woQuery.isLoading) return <Spinner label="Loading work order…" />;
  if (woQuery.error || !aggregate) return <ModernErrorBanner message="Could not find that work order." />;

  const { workOrder, laborLines, subletLines, estimate, invoices, technicians } = aggregate;
  const realPartLines = aggregate.partLines.filter((l) => l.lineKind !== "shop_supplies");
  // Shop Supplies always renders LAST, regardless of its lineNo (it syncs
  // immediately after the first real part line is added, so its lineNo
  // often lands in the middle — see docs/FRONTEND_BACKLOG.md).
  const shopSuppliesLine = aggregate.partLines.find((l) => l.lineKind === "shop_supplies") ?? null;
  const nextStep = NEXT_STEP[workOrder.status];
  const canVoid = workOrder.status !== "void" && workOrder.status !== "invoiced";
  // No stored "status before void" column — never having been completed/
  // invoiced is the proxy for "can be reopened" (see WorkOrdersService.unvoid);
  // un-voiding returns it to the estimate stage.
  const canUnvoid = workOrder.status === "void" && !workOrder.completedAt && !workOrder.invoicedAt;
  // A work order can only be deleted once it's void AND has no invoices —
  // same lifecycle gate WorkOrdersService.delete() enforces server-side.
  const canDelete = workOrder.status === "void" && invoices.length === 0;
  // Why un-void/delete aren't available right now — shown both in the
  // estimate-print banner below and (when it'd otherwise be the ONLY thing
  // to say, i.e. the void-work-order card would have nothing else to show)
  // inside that card itself, kept in one place so the two spots can't
  // drift out of sync with each other or with canUnvoid/canDelete above.
  const voidBlockedExplanation =
    workOrder.status === "void" && !canUnvoid
      ? canDelete
        ? "It can't be un-voided since it had already been completed."
        : `It can't be un-voided since it had already been completed, and it still has ${invoices.length === 1 ? "an invoice" : "invoices"} attached — delete the voided invoice${invoices.length === 1 ? "" : "s"} below (open it and use "Archive or delete…") to make this work order deletable.`
      : null;
  // The schema allows more than one invoice per work order (progress
  // billing / split payers), but that's for issuing a SECOND invoice once
  // the first is settled — never a second LIVE one alongside an already-
  // active one. Hidden while any non-void invoice exists; reappears once
  // every existing invoice is void (or deleted).
  const hasActiveInvoice = invoices.some((inv) => inv.status !== "void");
  // Mirrors the work-orders list query's own LATERAL join: prefer the
  // latest ACTIVE (non-void) invoice, falling back to the latest void one
  // only when every invoice on this work order is void, so a fully-voided
  // work order shows "Void" rather than looking never-invoiced. null means
  // no invoice has ever been issued.
  const billingStatus: InvoiceStatus | null =
    invoices.length === 0
      ? null
      : [...invoices].sort((a, b) => {
          const activeDiff = Number(b.status !== "void") - Number(a.status !== "void");
          return activeDiff !== 0 ? activeDiff : b.sequenceInWorkOrder - a.sequenceInWorkOrder;
        })[0]!.status;
  // Mirrors WorkOrdersService.assertHasBillableValue — an estimate/invoice
  // with nothing billed on it isn't a meaningful document to issue.
  const hasBillableValue = Number(workOrder.subtotal) > 0;
  // Once invoiced, the work order row is a permanent record of what was
  // billed — void the INVOICE (which reverts this back to 'complete') to
  // edit again, per WorkOrdersService.assertEditable.
  const isLocked = workOrder.status === "invoiced";
  // A reminder (never a requirement) to fill in what an inspection record needs.
  const hasInspectionLine = aggregate.partLines.some((l) => l.isStateInspection);
  // Vehicle fields are fixed on the vehicle's own Edit page; the odometers are on this page.
  const vehicleMissing: string[] = [];
  const odometerMissing: string[] = [];
  if (hasInspectionLine) {
    // The odometers live on this work order, so they're checked even while the vehicle is still loading.
    if (workOrder.odometerIn == null) odometerMissing.push("odometer in");
    if (workOrder.odometerOut == null) odometerMissing.push("odometer out");
    if (vehicleQuery.data) {
      if (!vehicleQuery.data.vehicle.vin) vehicleMissing.push("VIN");
      if (!vehicleQuery.data.vehicle.licensePlate) vehicleMissing.push("license plate");
    }
  }
  const inspectionMissingCount = vehicleMissing.length + odometerMissing.length;
  // See money.ts's own comment on formatTaxRateLabel's hasTaxableLine
  // param — a category with only individually-tax-exempt lines (or no
  // line at all) shouldn't show its rate next to Tax.
  const taxRateLabel = formatWorkOrderTaxRateLabel(
    workOrder,
    { parts: shopTypeLabels.partLabel, labor: shopTypeLabels.laborLabel, sublet: shopTypeLabels.subletLabel },
    {
      parts: aggregate.partLines.some((l) => l.isTaxable),
      labor: laborLines.some((l) => l.isTaxable),
      sublet: subletLines.some((l) => l.isTaxable),
    },
  );
  // "Tax" by default — live tenant_settings, since a work order isn't
  // invoiced yet and has nothing of its own to freeze (see
  // tenant_settings.taxLabel's own doc comment).
  const taxLabel = tenantSettingsQuery.data?.settings.taxLabel ?? "Tax";
  const busy =
    isLocked ||
    lifecycleMutation.isPending ||
    voidMutation.isPending ||
    unvoidMutation.isPending ||
    deleteMutation.isPending ||
    prepareEstimateMutation.isPending ||
    invoiceMutation.isPending ||
    deletePartLineMutation.isPending ||
    deleteLaborLineMutation.isPending ||
    deleteSubletLineMutation.isPending ||
    shopSuppliesMutation.isPending;

  const assignedTechnicianIds = new Set(technicians.map((t) => t.technicianId));
  const availableTechnicians = (techniciansQuery.data?.items ?? []).filter((t) => !assignedTechnicianIds.has(t.id));
  function technicianName(technicianId: number): string {
    return techniciansQuery.data?.items.find((t) => t.id === technicianId)?.displayName ?? `#${technicianId}`;
  }

  const concernValue = concern ?? workOrder.customerConcern ?? "";
  const odoInValue = odoIn ?? (workOrder.odometerIn != null ? String(workOrder.odometerIn) : "");
  // Defaults Out to whatever In shows, for a shop that drives the vehicle
  // the same short distance in/out the same day — the shop can still type
  // a different Out value, which then wins over this default. Only
  // defaults when there's neither an unsaved edit (odoOut) nor an already-
  // saved value (workOrder.odometerOut) to respect instead.
  const odoOutValue = odoOut ?? (workOrder.odometerOut != null ? String(workOrder.odometerOut) : odoInValue);
  const flexField1Value = flexField1 ?? workOrder.flexField1 ?? "";
  const flexField1Label = tenantSettingsQuery.data?.settings.flexField1Label ?? null;
  const internalNotesValue = internalNotesDraft ?? workOrder.internalNotes ?? "";
  const customerNotesValue = customerNotesDraft ?? workOrder.customerNotes ?? "";

  const breadcrumbItems = cameFromCustomer
    ? [
        { label: "Customers", to: "/customers" },
        ...(customerQuery.data
          ? [{ label: customerQuery.data.customer.displayName, to: `/customers/${customerQuery.data.customer.customerNumber}` }]
          : []),
        { label: `Work Order ${formatWorkOrderNumber(workOrder.workOrderNumber)}` },
      ]
    : [{ label: "Work Orders", to: "/work-orders" }, { label: `Work Order ${formatWorkOrderNumber(workOrder.workOrderNumber)}` }];

  const vehicleLine = vehicleQuery.data
    ? [vehicleQuery.data.vehicle.modelYear, vehicleQuery.data.vehicle.make, vehicleQuery.data.vehicle.model, vehicleQuery.data.vehicle.trim]
        .filter(Boolean)
        .join(" ")
    : null;
  const subtitleLine =
    [
      vehicleLine,
      vehicleQuery.data?.vehicle.licensePlate ? `Plate ${vehicleQuery.data.vehicle.licensePlate}` : null,
      vehicleQuery.data?.vehicle.vin ? `VIN ${vehicleQuery.data.vehicle.vin}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;
  const contactLine =
    [customerQuery.data?.customer.phones[0]?.phoneNumber, customerQuery.data?.customer.email].filter(Boolean).join(" · ") || null;


  return (
    <div>
      <ModernBreadcrumbs items={breadcrumbItems} />

      <div className="mb-4 min-w-0">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">
          Work order {formatWorkOrderNumber(workOrder.workOrderNumber)} · Created {formatDate(workOrder.openedAt)}
          {/* Older work orders invoiced without Complete have no completedAt; fall back to the invoice date. */}
          {(workOrder.completedAt ?? workOrder.invoicedAt) &&
            ` · Completed ${formatDate(workOrder.completedAt ?? workOrder.invoicedAt)}`}
        </Eyebrow>
        <h1 className="m-0 text-[27px] font-bold leading-tight text-[#071a33]">
          {customerQuery.data ? (
            <Link to={`/customers/${customerQuery.data.customer.customerNumber}`} className="text-[#071a33] no-underline hover:text-[#0f6d86]">
              {customerQuery.data.customer.displayName}
            </Link>
          ) : (
            `Work Order ${formatWorkOrderNumber(workOrder.workOrderNumber)}`
          )}
        </h1>
        {subtitleLine && <p className="m-0 mt-1.5 text-[13.5px] text-[#4a5361]">{subtitleLine}</p>}
        {contactLine && <p className="m-0 mt-1 text-[13px] text-[#6b7a8f]">{contactLine}</p>}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <ModernStatusPill status={workOrder.status === "invoiced" ? "complete" : workOrder.status} />
          {billingStatus == null ? (
            <ModernStatusPill status="not_invoiced" />
          ) : (
            <>
              <ModernStatusPill status="invoiced" />
              <ModernStatusPill status={billingStatus} />
            </>
          )}
          <ModernInfoTip text={STATUS_HELP} />
        </div>
      </div>

      {notice && <ModernErrorBanner message={notice} />}

      {workOrder.status !== "void" && <ModernStageTracker order={STAGE_ORDER} labels={STAGE_LABELS} status={workOrder.status} />}

      {isLocked && (
        <ModernErrorBanner message="This work order has been invoiced and can no longer be changed. Void the invoice below to make changes." />
      )}

      {inspectionMissingCount > 0 && !isLocked && (
        <div role="status" className="mb-4 rounded-[10px] border border-[#f3dca3] bg-[#fff8e6] px-4 py-3 text-[13.5px] text-[#6b4b00]">
          <strong>State inspection on this job.</strong> To keep your inspection records complete, add{" "}
          {vehicleMissing.length > 0 && (
            <>
              the vehicle's {vehicleMissing.join(", ")}{" "}
              {customerQuery.data && (
                <>
                  (
                  <Link
                    to={`/customers/${customerQuery.data.customer.customerNumber}/vehicles/${workOrder.vehicleId}/edit`}
                    className="font-semibold text-[#0f6d86] underline"
                  >
                    edit vehicle
                  </Link>
                  )
                </>
              )}
              {odometerMissing.length > 0 && ", "}
            </>
          )}
          {odometerMissing.length > 0 && <>{odometerMissing.join(", ")} below within this work order</>}. You can still save and invoice
          without {inspectionMissingCount === 1 ? "it" : "them"}.
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,.85fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* ---- Job lines: Parts / Labor / Sublet ---- */}
          <ModernCard className="min-w-0 p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eef1f6] px-[18px] py-[15px]">
              <h2 className="m-0 text-[15px] font-bold text-[#071a33]">Job lines</h2>
              <div className="flex flex-col gap-[6px]">
                {/* w-0 + min-w-full: spans exactly the three buttons below and never
                    widens the header (the label truncates instead). */}
                <ModernTintButton
                  type="button"
                  className="min-h-[0px] h-7 w-0 min-w-full truncate px-2 text-[11px]"
                  disabled={isLocked}
                  onClick={() => setPickingPredefined(true)}
                >
                  + Predefined Items ({shopTypeLabels.partLabel}/{shopTypeLabels.laborLabel}/{shopTypeLabels.subletLabel})
                </ModernTintButton>
                <div className="flex gap-[7px]">
                  <ModernButton
                    type="button"
                    className="h-9 min-h-0 px-3 text-[12.5px] font-bold uppercase tracking-wide"
                    disabled={isLocked}
                    onClick={() => setAddingPart(true)}
                  >
                    + {shopTypeLabels.partLabel}
                  </ModernButton>
                  <ModernButton
                    type="button"
                    className="h-9 min-h-0 px-3 text-[12.5px] font-bold uppercase tracking-wide"
                    disabled={isLocked}
                    onClick={() => setAddingLabor(true)}
                  >
                    + {shopTypeLabels.laborLabel}
                  </ModernButton>
                  <ModernButton
                    type="button"
                    className="h-9 min-h-0 px-3 text-[12.5px] font-bold uppercase tracking-wide"
                    disabled={isLocked}
                    onClick={() => setAddingSublet(true)}
                  >
                    + {shopTypeLabels.subletLabel}
                  </ModernButton>
                </div>
              </div>
            </div>

            {/* A single table, 3 groups (Parts/Labor/Sublet) as tbody
                sections with a group-header row each — matching the
                mockup's "Job lines" table exactly (see
                docs/FRONTEND_BACKLOG.md), not 3 separately-carded/bordered
                blocks like the previous round's attempt. Every row shares
                the same 5-column shape (description+sub / qty / price /
                amount / actions) even for Labor/Sublet, which have no real
                qty/price fields — those cells render empty rather than a
                dash, so the 3 groups stay visually consistent. Parts'
                own group-name cell doubles as the shared column-header row
                (no separate "Parts" bar above it, unlike Labor/Sublet
                further down, which have no shared header row to fold
                into). */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse text-[14px]">
                <thead>
                  <tr className="bg-[#f8fafc]">
                    <th className="border-b border-[#eef1f6] px-[18px] pb-2 pt-3 text-left font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]">
                      {shopTypeLabels.partLabel}
                    </th>
                    <th className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                      Qty
                    </th>
                    <th className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                      Price
                    </th>
                    <th className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                      Total
                    </th>
                    <th className="border-b border-[#eef1f6] py-2 pl-3 pr-[18px]" />
                  </tr>
                </thead>
                <tbody>
                  {realPartLines.map((line) => (
                    <tr key={line.id}>
                      <td className="border-b border-[#f1f4f9] px-[18px] py-3">
                        {line.partNumberSnapshot && !line.isStateInspection && (
                          <div
                            className="font-mono text-[14px] font-semibold text-[#101b2d]"
                            style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                          >
                            {line.partNumberSnapshot}
                          </div>
                        )}
                        <div className="text-[14px] text-[#101b2d]">{line.descriptionSnapshot}</div>
                        {line.isStateInspection && (
                          <div className="mt-0.5 text-[12px] text-[#0f6d86]">
                            State inspection
                            {line.inspection?.result ? ` · ${line.inspection.result.toUpperCase()}` : " · result not entered yet"}
                            {line.inspection?.virNumber ? ` · VIR # ${line.inspection.virNumber}` : ""}
                            {line.inspection?.inspectorName ? ` · ${line.inspection.inspectorName}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="border-b border-[#f1f4f9] px-2 py-3 text-right tabular-nums text-[#4a5361]">{formatQty(line.quantity)}</td>
                      <td className="border-b border-[#f1f4f9] px-2 py-3 text-right tabular-nums text-[#4a5361]">{formatMoney(line.unitListPrice)}</td>
                      <td className="border-b border-[#f1f4f9] px-2 py-3 text-right font-bold tabular-nums text-[#071a33]">
                        {formatMoney(line.extendedAmount)}
                        {!line.isTaxable && <div className="text-[11px] font-normal text-[#8b97a8]">Tax exempt</div>}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f4f9] py-3 pl-3 pr-[18px] text-right">
                        <button
                          type="button"
                          className="p-1.5 text-[13px] font-semibold text-[#0f6d86] disabled:opacity-50"
                          disabled={isLocked}
                          onClick={() => setEditingPartLine(line)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          aria-label="Remove this part"
                          className="p-1.5 text-[13px] font-semibold text-[#b3261e] disabled:opacity-50"
                          disabled={busy}
                          onClick={() => deletePartLineMutation.mutate(line.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {realPartLines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="border-b border-[#f1f4f9] px-[18px] py-3 text-[13.5px] text-[#8b97a8]">
                        No parts added yet.
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td
                      colSpan={5}
                      className="border-b border-[#eef1f6] bg-[#f8fafc] px-[18px] pb-2 pt-3 font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]"
                    >
                      {shopTypeLabels.laborLabel}
                    </td>
                  </tr>
                  {laborLines.map((line) => (
                    <tr key={line.id}>
                      <td className="border-b border-[#f1f4f9] px-[18px] py-3">
                        <div className="whitespace-pre-line text-[14px] text-[#101b2d]">{line.description}</div>
                      </td>
                      <td className="border-b border-[#f1f4f9] px-2 py-3" />
                      <td className="border-b border-[#f1f4f9] px-2 py-3" />
                      <td className="border-b border-[#f1f4f9] px-2 py-3 text-right font-bold tabular-nums text-[#071a33]">
                        {formatMoney(line.extendedAmount)}
                        {!line.isTaxable && <div className="text-[11px] font-normal text-[#8b97a8]">Tax exempt</div>}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f4f9] py-3 pl-3 pr-[18px] text-right">
                        <button
                          type="button"
                          className="p-1.5 text-[13px] font-semibold text-[#0f6d86] disabled:opacity-50"
                          disabled={isLocked}
                          onClick={() => setEditingLaborLine(line)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          aria-label="Remove this labor line"
                          className="p-1.5 text-[13px] font-semibold text-[#b3261e] disabled:opacity-50"
                          disabled={busy}
                          onClick={() => deleteLaborLineMutation.mutate(line.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {laborLines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="border-b border-[#f1f4f9] px-[18px] py-3 text-[13.5px] text-[#8b97a8]">
                        No labor added yet.
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td
                      colSpan={5}
                      className="border-b border-[#eef1f6] bg-[#f8fafc] px-[18px] pb-2 pt-3 font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {shopTypeLabels.subletLabel} <ModernInfoTip text={SUBLET_HELP} align="start" />
                      </span>
                    </td>
                  </tr>
                  {subletLines.map((line) => (
                    <tr key={line.id}>
                      <td className="border-b border-[#f1f4f9] px-[18px] py-3">
                        <div className="text-[14px] text-[#101b2d]">{line.description}</div>
                        {line.vendorName && (
                          <div
                            className="mt-0.5 font-mono text-[11.5px] leading-[1.4] text-[#8b97a8]"
                            style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                          >
                            {line.vendorName}
                          </div>
                        )}
                      </td>
                      <td className="border-b border-[#f1f4f9] px-2 py-3" />
                      <td className="border-b border-[#f1f4f9] px-2 py-3" />
                      <td className="border-b border-[#f1f4f9] px-2 py-3 text-right font-bold tabular-nums text-[#071a33]">
                        {formatMoney(line.priceAmount)}
                        {!line.isTaxable && <div className="text-[11px] font-normal text-[#8b97a8]">Tax exempt</div>}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f4f9] py-3 pl-3 pr-[18px] text-right">
                        <button
                          type="button"
                          className="p-1.5 text-[13px] font-semibold text-[#0f6d86] disabled:opacity-50"
                          disabled={isLocked}
                          onClick={() => setEditingSubletLine(line)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          aria-label="Remove this sublet line"
                          className="p-1.5 text-[13px] font-semibold text-[#b3261e] disabled:opacity-50"
                          disabled={busy}
                          onClick={() => deleteSubletLineMutation.mutate(line.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {subletLines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-[18px] py-3 text-[13.5px] text-[#8b97a8]">
                        No sublet work added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Shop Supplies has no mockup equivalent (real, existing
                functionality — automatic amount + custom-override on the
                PARTS subtotal) — kept below the table rather than dropped. */}
            {shopSuppliesEnabled && (
              <div className="border-t border-[#eef1f6] p-[18px]">
                <ModernShopSuppliesRow
                  line={shopSuppliesLine}
                  partsTotal={workOrder.partsTotal}
                  rateSnapshot={workOrder.shopSuppliesRateSnapshot}
                  overrideAmount={workOrder.shopSuppliesOverrideAmount}
                  cap={tenantSettingsQuery.data?.settings.shopSuppliesCap ?? null}
                  busy={busy}
                  onSetOverride={(amount) => shopSuppliesMutation.mutate(amount)}
                />
              </div>
            )}
            {hasBillableValue && (
              <div className="border-t border-[#eef1f6] p-[18px]">
                <ModernDiscountRow
                  type={workOrder.discountType}
                  value={workOrder.discountValue}
                  disabled={busy || isLocked || workOrder.status === "void"}
                  onSet={(values) => discountMutation.mutate(values)}
                />
              </div>
            )}
          </ModernCard>

          {/* ---- Job details ---- */}
          <ModernCard className="min-w-0">
            <h2 className="m-0 mb-3.5 text-[15px] font-bold text-[#071a33]">Work Order Details</h2>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
              <ModernField label="What's the Problem/Concern?" htmlFor="m-customerConcern" className="sm:col-span-3">
                <ModernTextarea id="m-customerConcern" rows={2} uppercase disabled={isLocked} value={concernValue} onChange={(e) => setConcern(e.target.value)} />
              </ModernField>
              {hasVehicles && (
                <ModernField label="Odometer In" htmlFor="m-odometerIn">
                  <ModernInput
                    id="m-odometerIn"
                    inputMode="numeric"
                    disabled={isLocked}
                    value={odoInValue}
                    onChange={(e) => setOdoIn(applyDigitPreservingTransform(e.target, (v) => v.replace(/\D/g, "")))}
                  />
                </ModernField>
              )}
              {hasVehicles && (
                <ModernField label="Odometer Out" htmlFor="m-odometerOut">
                  <ModernInput
                    id="m-odometerOut"
                    inputMode="numeric"
                    disabled={isLocked}
                    value={odoOutValue}
                    onChange={(e) => setOdoOut(applyDigitPreservingTransform(e.target, (v) => v.replace(/\D/g, "")))}
                  />
                </ModernField>
              )}
              {flexField1Label && (
                <ModernField label={flexField1Label} htmlFor="m-flexField1" className="sm:col-span-3">
                  <ModernInput id="m-flexField1" uppercase disabled={isLocked} value={flexField1Value} onChange={(e) => setFlexField1(e.target.value)} />
                </ModernField>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <ModernButton
                type="button"
                onClick={() =>
                  jobDetailsConfirm.request(() =>
                    jobDetailsMutation.mutate({
                      customerConcern: concernValue,
                      odometerIn: odoInValue.trim() ? Number(odoInValue) : null,
                      odometerOut: odoOutValue.trim() ? Number(odoOutValue) : null,
                      flexField1: flexField1Value.trim() || null,
                    }),
                  )
                }
                disabled={jobDetailsMutation.isPending || isLocked}
              >
                Save Work Order Details
              </ModernButton>
            </div>
          </ModernCard>

          {/* ---- Technicians ---- */}
          <ModernCard className="min-w-0">
            <h2 className="m-0 mb-3.5 text-[15px] font-bold text-[#071a33]">Technician(s) on This Work Order</h2>
            <div className="mb-3.5 flex flex-col gap-2">
              {technicians.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-[10px] border border-[#e2e7ee] px-3.5 py-3">
                  <span className="text-[14px] text-[#101b2d]">
                    {technicianName(t.technicianId)} <span className="capitalize text-[#6b7a8f]">({t.role})</span>
                  </span>
                  <button
                    type="button"
                    className="text-[13px] font-semibold text-[#b3261e] disabled:opacity-50"
                    disabled={busy}
                    onClick={() => unassignTechMutation.mutate(t.technicianId)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {technicians.length === 0 && <p className="m-0 text-[13.5px] text-[#8b97a8]">No technicians assigned yet.</p>}
            </div>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!technicianToAdd) return;
                assignConfirm.request(() => assignTechMutation.mutate(Number(technicianToAdd)));
              }}
            >
              <ModernField label="Add a Technician" htmlFor="m-technicianToAdd" className="!mb-0 flex-1">
                <ModernSelect id="m-technicianToAdd" disabled={isLocked} value={technicianToAdd} onChange={(e) => setTechnicianToAdd(e.target.value)}>
                  <option value="">Choose…</option>
                  {availableTechnicians.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.displayName}
                    </option>
                  ))}
                </ModernSelect>
              </ModernField>
              <ModernButton type="submit" disabled={!technicianToAdd || assignTechMutation.isPending || isLocked}>
                + Add
              </ModernButton>
            </form>
          </ModernCard>

          {/* ---- Notes ---- */}
          <ModernCard className="min-w-0">
            <h2 className="m-0 mb-3.5 text-[15px] font-bold text-[#071a33]">Notes</h2>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <div>
                <ModernField label="Customer-Visible Note" htmlFor="m-customerNotes">
                  <ModernTextarea
                    id="m-customerNotes"
                    rows={3}
                    placeholder="Prints on the invoice and estimate"
                    value={customerNotesValue}
                    disabled={isLocked}
                    onChange={(e) => setCustomerNotesDraft(e.target.value)}
                  />
                </ModernField>
                {isLocked && (
                  <p className="m-0 mb-3 text-[12.5px] text-[#8b97a8]">Read-only once invoiced — void the invoice to change it.</p>
                )}
                <ModernButton
                  type="button"
                  variant="secondary"
                  disabled={customerNotesMutation.isPending || isLocked}
                  onClick={() => notesConfirm.request(() => customerNotesMutation.mutate(customerNotesValue.trim() || null))}
                >
                  Save Customer Note
                </ModernButton>
              </div>
              <div>
                <ModernField label="Internal Note" htmlFor="m-internalNotes">
                  <ModernTextarea
                    id="m-internalNotes"
                    rows={3}
                    placeholder="Never printed — for shop staff only"
                    value={internalNotesValue}
                    onChange={(e) => setInternalNotesDraft(e.target.value)}
                  />
                </ModernField>
                {/* Always editable/savable, even once invoiced — see
                    internalNotesMutation's own comment. */}
                <ModernButton
                  type="button"
                  variant="secondary"
                  disabled={internalNotesMutation.isPending}
                  onClick={() => notesConfirm.request(() => internalNotesMutation.mutate(internalNotesValue.trim() || null))}
                >
                  Save Internal Note
                </ModernButton>
              </div>
            </div>
          </ModernCard>

        </div>

        {/* ---- Right rail ---- */}
        <aside className="flex min-w-0 flex-col gap-3.5 lg:sticky lg:top-4">
          <ModernCard dark>
            <Eyebrow className="text-[11px] text-[#7fd3e2]">Work Order Total</Eyebrow>
            <div className="mt-2.5 text-[32px] font-bold leading-none tabular-nums text-white">{formatMoney(workOrder.total)}</div>
            <dl className="m-0 mt-4 grid grid-cols-[1fr_auto] gap-y-1.5 text-[13.5px]">
              {workOrder.partsTotal !== "0.00" && (
                <>
                  <dt className="text-[#9fb6cf]">{shopTypeLabels.partLabel}</dt>
                  <dd className="m-0 text-right tabular-nums text-white">{formatMoney(workOrder.partsTotal)}</dd>
                </>
              )}
              {workOrder.laborTotal !== "0.00" && (
                <>
                  <dt className="text-[#9fb6cf]">{shopTypeLabels.laborLabel}</dt>
                  <dd className="m-0 text-right tabular-nums text-white">{formatMoney(workOrder.laborTotal)}</dd>
                </>
              )}
              {workOrder.subletTotal !== "0.00" && (
                <>
                  <dt className="text-[#9fb6cf]">{shopTypeLabels.subletLabel}</dt>
                  <dd className="m-0 text-right tabular-nums text-white">{formatMoney(workOrder.subletTotal)}</dd>
                </>
              )}
              {(shopSuppliesLine?.extendedAmount ?? "0.00") !== "0.00" && (
                <>
                  <dt className="text-[#9fb6cf]">Shop supplies ({(Number(workOrder.shopSuppliesRateSnapshot) * 100).toFixed(0)}%)</dt>
                  <dd className="m-0 text-right tabular-nums text-white">{formatMoney(shopSuppliesLine?.extendedAmount ?? ("0.00" as Money))}</dd>
                </>
              )}
              <dt className="border-t border-white/15 pt-2 text-[#9fb6cf]">Subtotal</dt>
              <dd className="m-0 border-t border-white/15 pt-2 text-right tabular-nums text-white">{formatMoney(workOrder.subtotal)}</dd>
              {workOrder.discountTotal !== "0.00" && (
                <>
                  <dt className="text-[#9fb6cf]">
                    Discount{workOrder.discountType === "percent" && workOrder.discountValue ? ` (${Number(workOrder.discountValue)}%)` : ""}
                  </dt>
                  <dd className="m-0 text-right tabular-nums text-white">−{formatMoney(workOrder.discountTotal)}</dd>
                </>
              )}
              <dt className="text-[#9fb6cf]">
                {taxLabel}
                {taxRateLabel ? ` ${taxRateLabel}` : ""}
              </dt>
              <dd className="m-0 text-right tabular-nums text-white">{formatMoney(workOrder.taxTotal)}</dd>
            </dl>
          </ModernCard>

          {/* Approving needs at least one part/labor/sublet line (shop supplies is auto-added, so doesn't count). */}
          {nextStep && (nextStep.action !== "approve" || realPartLines.length + laborLines.length + subletLines.length > 0) && (
            <ModernCard>
              <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Next step</h2>
              <ModernButton
                className="w-full"
                disabled={busy}
                onClick={() => lifecycleConfirm.request(() => lifecycleMutation.mutate(nextStep.action))}
              >
                {nextStep.label}
              </ModernButton>
              {nextStep.action === "approve" && (
                <p className="m-0 mt-2 text-[12.5px] text-[#8b97a8]">You can create invoices on approved work orders only.</p>
              )}
            </ModernCard>
          )}

          <ModernCard>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[14px] font-bold text-[#071a33]">Send &amp; print</h2>
              {estimate && (
                <ModernInfoTip text="Estimates always reflect this work order's CURRENT lines and totals — printing or downloading again after a change shows the update immediately." />
              )}
            </div>
            {!hasBillableValue && workOrder.status !== "void" && (
              <p className="m-0 mb-3 text-[13px] text-[#8b97a8]">
                Add at least one {shopTypeLabels.partLabel.toLowerCase()}, {shopTypeLabels.laborLabel.toLowerCase()}, or{" "}
                {shopTypeLabels.subletLabel.toLowerCase()} charge with a value before preparing an estimate or creating an
                invoice.
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              {hasBillableValue && workOrder.status !== "void" && (
                <>
                  <div className="flex gap-1.5">
                    <ModernButton
                      type="button"
                      variant="secondary"
                      className="min-w-0 flex-1 justify-start"
                      disabled={printingEstimate || prepareEstimateMutation.isPending}
                      onClick={requestEstimatePrint}
                    >
                      <PrintIcon />
                      {prepareEstimateMutation.isPending ? "Preparing…" : "Print Estimate"}
                    </ModernButton>
                    <ModernButton
                      type="button"
                      variant="secondary"
                      className="shrink-0 justify-center !px-3"
                      title="Download Estimate"
                      aria-label="Download Estimate"
                      disabled={prepareEstimateMutation.isPending}
                      onClick={requestEstimateDownload}
                    >
                      <DownloadIcon />
                    </ModernButton>
                  </div>
                  {canEmailEstimate && (
                    <ModernButton
                      type="button"
                      variant="secondary"
                      className="w-full justify-start"
                      disabled={prepareEstimateMutation.isPending}
                      onClick={requestEstimateEmail}
                    >
                      <EmailIcon />
                      Email Estimate to Customer
                    </ModernButton>
                  )}
                  {!canEmailEstimate && customerQuery.data && shopProfileQuery.data && (
                    <EmailSetupHint
                      kindLabel="estimate"
                      customerMissing={!customerEmail}
                      shopMissing={!shopProfileQuery.data.shopProfile.email}
                      customerNumber={customerQuery.data.customer.customerNumber}
                    />
                  )}
                  {canEmailEstimate && estimateEmailsQuery.data && estimateEmailsQuery.data.count > 0 && (
                    <p className="m-0 text-[12.5px] text-[#8b97a8]">
                      Emailed {estimateEmailsQuery.data.count}× · last to {estimateEmailsQuery.data.lastSentTo} on{" "}
                      {formatDateTime(estimateEmailsQuery.data.lastSentAt)}
                    </p>
                  )}
                </>
              )}
            </div>
            {!estimateDocumentReady && workOrder.status === "void" && (
              <p className="m-0 mt-3 text-[13px] text-[#8b97a8]">
                {canUnvoid
                  ? "Viewing and printing are disabled while this work order is void — un-void it to view or print the estimate."
                  : `Viewing and printing are disabled while this work order is void. ${voidBlockedExplanation}`}
              </p>
            )}
            {/* Hidden — not a preview, just the document the Print estimate
                button loads and calls contentWindow.print() on once ready.
                No src until clicked, so viewing this page never triggers a
                PDF render the shop didn't ask for. */}
            <iframe
              ref={estimatePrintFrameRef}
              title="Estimate print"
              src={estimatePrintToken ? `${workOrdersApi.estimateDocumentUrl(id)}?t=${estimatePrintToken}` : undefined}
              onLoad={() => {
                if (estimatePrintToken) estimatePrintFrameRef.current?.contentWindow?.print();
                setPrintingEstimate(false);
              }}
              style={{ position: "absolute", width: 0, height: 0, border: 0, left: "-9999px" }}
            />
          </ModernCard>

          {/* Shown whenever there's something to do here: existing invoices
              to view, or a fresh one eligible to be created — not gated on
              invoices.length so "Create invoice" has somewhere to live
              before the first one exists. */}
          {(invoices.length > 0 || (workOrder.approvedAt && !hasActiveInvoice && workOrder.status !== "void")) && (
            <ModernCard>
              <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Invoices</h2>
              {invoices.length > 0 && (
                <div className="mb-3 flex flex-col gap-2">
                  {invoices.map((inv) => (
                    <Link
                      key={inv.id}
                      to={`/invoices/${inv.invoiceNumber}`}
                      state={{ from: `/work-orders/${workOrderNumber}` }}
                      className={`flex items-center justify-between rounded-[10px] border px-3.5 py-3 no-underline ${
                        inv.status === "void" ? "border-[#f3d7d5] bg-[#fdecea]" : "border-[#e2e7ee] bg-[#f6f8fb]"
                      }`}
                    >
                      <span className="flex items-center gap-2 text-[13.5px] text-[#101b2d]">
                        <InvoiceIcon />
                        Invoice {formatInvoiceNumber(inv.invoiceNumber)}
                      </span>
                      <span className="flex items-center gap-2.5">
                        <span className="text-[13.5px] font-bold tabular-nums text-[#071a33]">{formatMoney(inv.total)}</span>
                        <ModernStatusPill status={inv.status} />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
              {workOrder.approvedAt && !hasActiveInvoice && workOrder.status !== "void" && (
                <ModernButton
                  className="w-full"
                  disabled={busy || !hasBillableValue}
                  onClick={() => invoiceConfirm.request(() => invoiceMutation.mutate())}
                >
                  Create invoice
                </ModernButton>
              )}
            </ModernCard>
          )}

          {(canVoid || canUnvoid || canDelete || workOrder.status === "void") && (
            <ModernCard>
              <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Void work order</h2>
              <div className="flex flex-col gap-2">
                {!canUnvoid && !canDelete && workOrder.status === "void" && (
                  <p className="m-0 text-[13px] text-[#8b97a8]">{voidBlockedExplanation}</p>
                )}
                {canUnvoid && (
                  <ModernButton
                    variant="secondary"
                    className="w-full"
                    disabled={busy}
                    onClick={() => unvoidConfirm.request(() => unvoidMutation.mutate())}
                  >
                    Un-Void (Back to Estimate Stage)
                  </ModernButton>
                )}
                {canVoid && (
                  <ModernConfirmButton
                    variant="danger"
                    className="w-full"
                    confirmText="Void this whole work order?"
                    disabled={busy}
                    onConfirm={() => voidMutation.mutate("Voided from the work order screen.")}
                  >
                    Void Work Order
                  </ModernConfirmButton>
                )}
                {canDelete && (
                  <ModernConfirmButton
                    variant="danger"
                    className="w-full"
                    confirmText="Permanently delete this work order?"
                    disabled={busy}
                    onConfirm={() => deleteMutation.mutate()}
                  >
                    Delete Work Order
                  </ModernConfirmButton>
                )}
              </div>
            </ModernCard>
          )}
        </aside>
      </div>

      {pickingPredefined && <PredefinedItemsPopup onPick={addFromPredefined} onClose={() => setPickingPredefined(false)} />}
      {addingPart && (
        <AddPartLineModal
          initialItem={prefillItem ?? undefined}
          shopSuppliesEnabled={shopSuppliesEnabled}
          taxRate={workOrder.partsTaxRateSnapshot}
          onClose={() => {
            setAddingPart(false);
            setPrefillItem(null);
          }}
          onSave={async (values) => {
            await addPartMutation.mutateAsync(values);
          }}
        />
      )}
      {addingLabor && (
        <AddLaborLineModal
          initialItem={prefillItem ?? undefined}
          taxRate={workOrder.laborTaxRateSnapshot}
          onClose={() => {
            setAddingLabor(false);
            setPrefillItem(null);
          }}
          onSave={async (values) => {
            await addLaborMutation.mutateAsync(values);
          }}
        />
      )}
      {addingSublet && (
        <AddSubletLineModal
          initialItem={prefillItem ?? undefined}
          taxRate={workOrder.subletTaxRateSnapshot}
          onClose={() => {
            setAddingSublet(false);
            setPrefillItem(null);
          }}
          onSave={async (values) => {
            await addSubletMutation.mutateAsync(values);
          }}
        />
      )}
      {editingPartLine && (
        <EditPartLineModal
          line={editingPartLine}
          shopSuppliesEnabled={shopSuppliesEnabled}
          taxRate={workOrder.partsTaxRateSnapshot}
          inspectors={(techniciansQuery.data?.items ?? []).filter((t) => t.isActive || t.id === editingPartLine.inspection?.inspectorTechnicianId)}
          onClose={() => setEditingPartLine(null)}
          onSave={async (values) => {
            await updatePartLineMutation.mutateAsync({ lineId: editingPartLine.id, ...values });
          }}
        />
      )}
      {editingLaborLine && (
        <EditLaborLineModal
          line={editingLaborLine}
          taxRate={workOrder.laborTaxRateSnapshot}
          onClose={() => setEditingLaborLine(null)}
          onSave={async (values) => {
            await updateLaborLineMutation.mutateAsync({ lineId: editingLaborLine.id, ...values });
          }}
        />
      )}
      {editingSubletLine && (
        <EditSubletLineModal
          line={editingSubletLine}
          taxRate={workOrder.subletTaxRateSnapshot}
          onClose={() => setEditingSubletLine(null)}
          onSave={async (values) => {
            await updateSubletLineMutation.mutateAsync({ lineId: editingSubletLine.id, ...values });
          }}
        />
      )}

      {emailDialogOpen && (
        <EmailDocumentDialog
          kindLabel="estimate"
          queryKey={["document-emails", "estimate", id]}
          loadPreview={() => workOrdersApi.estimateEmailPreview(id)}
          send={() => workOrdersApi.emailEstimate(id)}
          onSent={() => void queryClient.invalidateQueries({ queryKey: ["document-emails", "estimate", id] })}
          onClose={() => setEmailDialogOpen(false)}
        />
      )}
      <ModernConfirmDialog {...lifecycleConfirm.dialogProps} message={nextStep?.confirm ?? ""} />
      <ModernConfirmDialog
        {...invoiceConfirm.dialogProps}
        message="Create an invoice with the current totals? This locks in these numbers and marks the work order completed."
      />
      <ModernConfirmDialog {...unvoidConfirm.dialogProps} message="Un-void this work order and put it back at the estimate stage?" />
      <ModernConfirmDialog {...jobDetailsConfirm.dialogProps} message="Save these work order details?" />
      <ModernConfirmDialog {...notesConfirm.dialogProps} message="Save these notes?" />
      <ModernConfirmDialog {...assignConfirm.dialogProps} message="Assign this technician to the work order?" />
    </div>
  );
}

/**
 * Modern-only "+ Part" modal, replacing the always-visible inline add-form
 * Classic keeps (never touch Classic's own file) — matches the mockup's
 * "Job lines" card, whose header holds small `+ Part/+ Labor/+ Sublet`
 * buttons rather than permanent form rows. Same field set/validation
 * messages/defaults as the inline form it replaces (Part Number,
 * Description, Qty default "1", optional Price, Include in Shop Supplies
 * default checked) — self-contained state/confirm-gate, same pattern as
 * EditPartLineModal.tsx just used from the Edit side of this same page.
 */
/** The in-form "Choose from predefined items" search field is hidden for now — the
 * "+ Predefined Items" popup above the Job lines buttons replaces it. Flip to bring it back. */
const SHOW_FORM_PICKER = false;

/** Values for adding a predefined item as a work-order line as-is, or null when it
 * lacks something the line needs (a part title, a labor/sublet price) — the caller then
 * opens the Add modal prefilled so it can be typed in. */
function partValuesFromItem(item: PredefinedItem, taxRate: string) {
  const inspection = item.data.stateInspection === true;
  const partNumber = item.data.partNumber ?? (inspection ? "INSPECTION" : "");
  if (!partNumber) return null;
  const exempt = inspection || (item.data.taxExempt === true && Number(taxRate) > 0);
  return {
    partNumber,
    description: item.data.description,
    quantity: "1" as Qty,
    unitListPrice: item.data.price as Money | undefined,
    includeInShopSupplies: !(inspection || item.data.noShopSupplies === true),
    isTaxable: exempt ? false : undefined,
    ...(inspection ? { isStateInspection: true } : {}),
  };
}
function laborValuesFromItem(item: PredefinedItem, taxRate: string) {
  if (!item.data.price) return null;
  const exempt = item.data.taxExempt === true && Number(taxRate) > 0;
  return { description: item.data.description, extendedAmount: item.data.price as Money, isTaxable: exempt ? false : undefined };
}
function subletValuesFromItem(item: PredefinedItem, taxRate: string) {
  if (!item.data.price) return null;
  const exempt = item.data.taxExempt === true && Number(taxRate) > 0;
  return {
    description: item.data.description,
    vendorName: item.data.vendorName,
    priceAmount: item.data.price as Money,
    isTaxable: exempt ? false : undefined,
  };
}

/** "+ Predefined Items" popup: every active catalog item, grouped like Settings >
 * Predefined Items (only the categories that have items, no edit/deactivate/delete).
 * Clicking a row calls `onPick`; a failure is shown here and the popup stays open. */
function PredefinedItemsPopup({ onPick, onClose }: { onPick: (item: PredefinedItem) => Promise<void>; onClose: () => void }) {
  const labels = useShopTypeLabels();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Same key/fn as PredefinedItemPicker's empty search, so the two share a cache entry.
  const listFor = (type: "part" | "labor" | "sublet") => ({
    queryKey: ["predefined-items", "picker", type, ""],
    queryFn: () => predefinedItemsApi.list({ type, limit: 100 }),
  });
  const partsQuery = useQuery(listFor("part"));
  const laborQuery = useQuery(listFor("labor"));
  const subletQuery = useQuery(listFor("sublet"));
  const groups = [
    { type: "part" as const, label: labels.partLabel, query: partsQuery },
    { type: "labor" as const, label: labels.laborLabel, query: laborQuery },
    { type: "sublet" as const, label: labels.subletLabel, query: subletQuery },
  ];
  const loading = groups.some((g) => g.query.isLoading);
  const failed = groups.some((g) => g.query.isError);
  const shown = groups.filter((g) => (g.query.data?.items.length ?? 0) > 0);

  async function pick(item: PredefinedItem): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onPick(item);
    } catch {
      setError("Could not add this item. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModernModal title="Predefined items" onCancel={onClose} wide>
      {error && <ModernErrorBanner message={error} />}
      {failed && <ModernErrorBanner message="Could not load predefined items." />}
      {loading && <Spinner />}
      {!loading && !failed && shown.length === 0 && (
        <p className="m-0 text-[13.5px] text-[#8b97a8]">No predefined items yet. Add some under Settings &gt; Predefined items.</p>
      )}
      <div className="max-h-[60vh] overflow-y-auto rounded-[10px] border border-[#eef1f6]">
        {shown.map(({ type, label, query }) => (
          <div key={type}>
            <div className="border-b border-[#eef1f6] bg-[#f8fafc] px-4 pb-2 pt-3 font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]">
              {label}
            </div>
            {query.data!.items.map((item) => {
              const title = type === "part" ? item.data.partNumber : type === "sublet" ? item.data.vendorName : undefined;
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={busy}
                  onClick={() => void pick(item)}
                  className="flex w-full items-start justify-between gap-3 border-b border-[#f1f4f9] px-4 py-3 text-left hover:bg-[#f1f4f9] disabled:opacity-60"
                >
                  <span className="min-w-0">
                    {title && <span className="block font-mono text-[14px] font-semibold text-[#101b2d]">{title}</span>}
                    <span className="block text-[14px] text-[#101b2d]">{item.data.description}</span>
                    {item.data.stateInspection ? (
                      <span className="block text-[11px] font-semibold text-[#0f6d86]">State inspection · tax exempt · no shop supplies</span>
                    ) : (
                      <>
                        {item.data.taxExempt && <span className="block text-[11px] text-[#8b97a8]">Tax exempt</span>}
                        {item.data.noShopSupplies && <span className="block text-[11px] text-[#8b97a8]">No shop supplies</span>}
                      </>
                    )}
                  </span>
                  <span className="flex-none tabular-nums text-[#4a5361]">{item.data.price ? formatMoney(item.data.price) : "—"}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </ModernModal>
  );
}

/** Shared by the three Add-line modals below — an optional search-and-pick
 * step (Settings > Predefined Items) sitting above each modal's own
 * manual-entry fields. Picking a result just prefills those fields via
 * `onPick`; nothing here creates a reference back to the predefined item,
 * so manual entry (ignoring this control entirely) behaves exactly as it
 * always has. Same search-then-click idiom as NewWorkOrderPageModern's
 * customer picker (a live useQuery keyed on the typed text, no debounce —
 * this app's per-tenant catalogs are small).
 *
 * Fetches unconditionally (not gated on the field being focused) for two
 * reasons: it's the only way to know up front whether this category has
 * ANY predefined items at all, which decides whether to render the field
 * below in the first place; and it keeps `results.data` populated across
 * a blur/refocus (e.g. picking one item, then focusing back in to pick a
 * different one) instead of leaving a window where a disabled query's
 * data could read as stale/empty right as the field reopens. */
function PredefinedItemPicker({
  type,
  onPick,
}: {
  type: "part" | "labor" | "sublet";
  onPick: (item: PredefinedItem) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const results = useQuery({
    queryKey: ["predefined-items", "picker", type, search],
    queryFn: () => predefinedItemsApi.list({ type, search: search.trim() || undefined, limit: 100 }),
  });

  // Nothing to search, and nothing typed either — this category has no
  // predefined items at all, so the field would only ever show "No
  // predefined items yet." Skip it entirely rather than clutter the form.
  if (!search.trim() && results.data && results.data.items.length === 0) {
    return null;
  }

  return (
    <ModernField label="Choose from predefined items" htmlFor={`predefinedItemSearch-${type}`}>
      <div className="relative">
        <ModernInput
          id={`predefinedItemSearch-${type}`}
          placeholder="Start typing… (optional — you can still type the line below by hand)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          // Picking an item closes the dropdown (setOpen(false)) but the
          // input itself never loses focus — the list button's onMouseDown
          // preventDefault()s specifically to stop that (see its own
          // comment below), so a second click on this SAME already-focused
          // input never fires onFocus again (focus events only fire on a
          // change of focus). onClick fires on every click regardless, so
          // it reopens the list without needing a blur-then-refocus first.
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") e.currentTarget.blur();
          }}
          autoComplete="off"
        />
        {open && results.data && results.data.items.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1 flex max-h-64 flex-col gap-1 overflow-y-auto rounded-[10px] border border-[#e2e7ee] bg-white p-1.5 shadow-[0_12px_30px_rgba(0,0,0,.12)]">
            {results.data.items.map((item) => (
              <button
                key={item.id}
                type="button"
                // Fires before the input's onBlur, so the click registers
                // before the dropdown closes out from under it.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(item);
                  setSearch("");
                  setOpen(false);
                }}
                className="rounded-[8px] px-3 py-2 text-left text-[13.5px] text-[#101b2d] hover:bg-[#f1f4f9]"
              >
                {item.data.partNumber && <span className="font-mono font-semibold">{item.data.partNumber} — </span>}
                {item.data.description}
                {item.data.vendorName && <span className="text-[#6b7a8f]"> ({item.data.vendorName})</span>}
                {item.data.price && <span className="float-right tabular-nums text-[#6b7a8f]">{formatMoney(item.data.price)}</span>}
              </button>
            ))}
          </div>
        )}
        {/* Only reachable with a non-empty search — the component returns
            null above once it's confirmed there are zero predefined items
            AND nothing's been typed, so this can't be "no items exist at
            all," only "nothing matches this search." */}
        {open && search.trim() && results.data && results.data.items.length === 0 && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-[10px] border border-[#e2e7ee] bg-white px-3 py-2 text-[13px] text-[#8b97a8] shadow-[0_12px_30px_rgba(0,0,0,.12)]">
            No match.
          </div>
        )}
      </div>
    </ModernField>
  );
}

function AddPartLineModal({
  initialItem,
  shopSuppliesEnabled,
  taxRate,
  onSave,
  onClose,
}: {
  /** Set when opened from the Predefined Items popup with an item that needs more input. */
  initialItem?: PredefinedItem;
  shopSuppliesEnabled: boolean;
  // The work order's current partsTaxRateSnapshot — the "Tax exempt"
  // checkbox only makes sense to show when this category is actually
  // taxed at all (see docs/SHOP_TYPES_PLAN.md-adjacent tax-rate-line
  // reasoning in apps/web/src/lib/money.ts).
  taxRate: string;
  onSave: (values: {
    partNumber: string;
    description: string;
    quantity: Qty;
    unitListPrice: Money | undefined;
    includeInShopSupplies: boolean;
    isTaxable?: boolean;
    isStateInspection?: boolean;
  }) => Promise<void>;
  onClose: () => void;
}) {
  const initialInspection = initialItem?.data.stateInspection === true;
  const [partNumber, setPartNumber] = useState(initialItem?.data.partNumber ?? (initialInspection ? "INSPECTION" : ""));
  const [description, setDescription] = useState(initialItem?.data.description ?? "");
  const [quantity, setQuantity] = useState("1");
  const [price, setPrice] = useState(initialItem?.data.price ?? "");
  const [includeInShopSupplies, setIncludeInShopSupplies] = useState(
    !(initialInspection || initialItem?.data.noShopSupplies === true),
  );
  // Unchecked (taxable) by default — omitting isTaxable entirely on save
  // lets the API resolve its own default (shop's partsTaxRate > 0), same
  // as never touching this checkbox at all; only an explicit check sends
  // an override. See WorkOrdersService.addPartLine/defaultTaxableFor.
  const [taxExempt, setTaxExempt] = useState(initialInspection || initialItem?.data.taxExempt === true);
  // Set by picking a predefined item flagged as a state inspection: the line is then
  // always tax exempt + outside shop supplies (the API enforces it too).
  const [isStateInspection, setIsStateInspection] = useState(initialInspection);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    const qty = parseQtyInput(quantity || "1");
    if (!qty) return setError('Quantity should look like "1" or "2.5".');
    const unitListPrice = price.trim() ? (parseMoneyInput(price) ?? undefined) : undefined;
    if (price.trim() && !unitListPrice) return setError('Price should look like "42.50".');
    if (!description.trim()) return setError("Description is required.");
    setError(null);
    setSaving(true);
    try {
      await onSave({
        partNumber,
        description: description.trim(),
        quantity: qty,
        unitListPrice,
        includeInShopSupplies: isStateInspection ? false : includeInShopSupplies,
        isTaxable: isStateInspection ? false : showTaxExempt && taxExempt ? false : undefined,
        ...(isStateInspection ? { isStateInspection: true } : {}),
      });
      onClose();
    } catch {
      setError("Could not add this part. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModernModal title={`Add ${shopTypeLabels.partLabel}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      {SHOW_FORM_PICKER && (
        <PredefinedItemPicker
          type="part"
          onPick={(item) => {
            const inspection = item.data.stateInspection === true;
            // A catalog part number is required on a part line; an inspection has no real one.
            setPartNumber(item.data.partNumber ?? (inspection ? "INSPECTION" : ""));
            setDescription(item.data.description);
            if (item.data.price) setPrice(item.data.price);
            setIsStateInspection(inspection);
            setTaxExempt(inspection || item.data.taxExempt === true);
            setIncludeInShopSupplies(!(inspection || item.data.noShopSupplies === true));
          }}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirmGate.request(() => void save());
        }}
      >
        <ModernField label={shopTypeLabels.partLabel} htmlFor="addPartNumber" required>
          <ModernInput id="addPartNumber" required uppercase value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
        </ModernField>
        <ModernField label="Description" htmlFor="addPartDescription" required>
          <ModernTextarea id="addPartDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        <ModernField label="Qty" htmlFor="addPartQty">
          <ModernInput id="addPartQty" decimal value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </ModernField>
        <ModernField label="Price" htmlFor="addPartPrice">
          <ModernInput id="addPartPrice" decimal placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} />
          {isStateInspection && (
            <p className="m-0 mt-1.5 text-[12px] text-[#0f6d86]">
              State inspection: always tax exempt and never charged shop supplies. Result and VIR # can be entered after adding it (Edit).
            </p>
          )}
          {!isStateInspection && shopSuppliesEnabled && (
            <ModernCheckboxRow className="mt-1.5" checked={includeInShopSupplies} onChange={setIncludeInShopSupplies}>
              Include in Shop Supplies
            </ModernCheckboxRow>
          )}
          {!isStateInspection && showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving || !partNumber.trim() || !description.trim()}>
            {saving ? "Adding…" : `Add ${shopTypeLabels.partLabel}`}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Add this ${shopTypeLabels.partLabel.toLowerCase()} to the work order?`} />
    </ModernModal>
  );
}

/** Modern-only "+ Labor" modal — see AddPartLineModal's own doc comment
 * for the overall pattern. Same field set/validation as the inline form
 * it replaces (What Was Done, Amount). */
function AddLaborLineModal({
  initialItem,
  taxRate,
  onSave,
  onClose,
}: {
  initialItem?: PredefinedItem;
  taxRate: string;
  onSave: (values: { description: string; extendedAmount: Money; isTaxable?: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const [description, setDescription] = useState(initialItem?.data.description ?? "");
  const [amount, setAmount] = useState(initialItem?.data.price ?? "");
  const [taxExempt, setTaxExempt] = useState(initialItem?.data.taxExempt === true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    const extendedAmount = parseMoneyInput(amount);
    if (!extendedAmount) return setError('Amount should look like "265.50".');
    setError(null);
    setSaving(true);
    try {
      await onSave({ description, extendedAmount, isTaxable: showTaxExempt && taxExempt ? false : undefined });
      onClose();
    } catch {
      setError("Could not add this labor line. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModernModal title={`Add ${shopTypeLabels.laborLabel}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      {SHOW_FORM_PICKER && (
        <PredefinedItemPicker
          type="labor"
          onPick={(item) => {
            setDescription(item.data.description);
            if (item.data.price) setAmount(item.data.price);
            setTaxExempt(item.data.taxExempt === true);
          }}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirmGate.request(() => void save());
        }}
      >
        <ModernField label="What Was Done" htmlFor="addLaborDescription" required>
          <ModernTextarea id="addLaborDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        <ModernField label="Amount" htmlFor="addLaborAmount" required>
          <ModernInput id="addLaborAmount" decimal required placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          {showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving}>
            {saving ? "Adding…" : `Add ${shopTypeLabels.laborLabel}`}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Add this ${shopTypeLabels.laborLabel.toLowerCase()} line to the work order?`} />
    </ModernModal>
  );
}

/** Modern-only "+ Sublet" modal — see AddPartLineModal's own doc comment
 * for the overall pattern. Same field set/validation as the inline form
 * it replaces (What Was It, Who Did the Work (optional), Amount Charged). */
function AddSubletLineModal({
  initialItem,
  taxRate,
  onSave,
  onClose,
}: {
  initialItem?: PredefinedItem;
  taxRate: string;
  onSave: (values: { description: string; vendorName: string | undefined; priceAmount: Money; isTaxable?: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const [description, setDescription] = useState(initialItem?.data.description ?? "");
  const [vendor, setVendor] = useState(initialItem?.data.vendorName ?? "");
  const [amount, setAmount] = useState(initialItem?.data.price ?? "");
  const [taxExempt, setTaxExempt] = useState(initialItem?.data.taxExempt === true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    const priceAmount = parseMoneyInput(amount);
    if (!priceAmount) return setError('Amount should look like "150.00".');
    setError(null);
    setSaving(true);
    try {
      await onSave({
        description,
        vendorName: vendor.trim() || undefined,
        priceAmount,
        isTaxable: showTaxExempt && taxExempt ? false : undefined,
      });
      onClose();
    } catch {
      setError("Could not add this sublet line. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModernModal title={`Add ${shopTypeLabels.subletLabel}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      {SHOW_FORM_PICKER && (
        <PredefinedItemPicker
          type="sublet"
          onPick={(item) => {
            setDescription(item.data.description);
            setVendor(item.data.vendorName ?? "");
            if (item.data.price) setAmount(item.data.price);
            setTaxExempt(item.data.taxExempt === true);
          }}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          confirmGate.request(() => void save());
        }}
      >
        <ModernField label="What Was It" htmlFor="addSubletDescription" required>
          <ModernTextarea id="addSubletDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        <ModernField label="Who Did the Work" htmlFor="addSubletVendor">
          <ModernInput id="addSubletVendor" placeholder="Optional" uppercase value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </ModernField>
        <ModernField label="Amount Charged" htmlFor="addSubletAmount" required>
          <ModernInput id="addSubletAmount" decimal required placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          {showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving}>
            {saving ? "Adding…" : `Add ${shopTypeLabels.subletLabel}`}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Add this ${shopTypeLabels.subletLabel.toLowerCase()} line to the work order?`} />
    </ModernModal>
  );
}

/** Modern counterpart of WorkOrderEditorPageClassic.tsx's own (private)
 * `ShopSuppliesRow` — same props/logic (parse + cap-check the custom
 * amount client-side, gate every change behind a confirm dialog, server
 * re-checks the cap for real), restyled. See Classic's own comments on
 * `basisAmount`/`isRemoved`/`isOverridden` for why each is computed the way
 * it is — unchanged here. */
function ModernShopSuppliesRow({
  line,
  partsTotal,
  rateSnapshot,
  overrideAmount,
  cap,
  busy,
  onSetOverride,
}: {
  line: WorkOrderPartLine | null;
  partsTotal: Money;
  rateSnapshot: string;
  overrideAmount: Money | null;
  cap: Money | null;
  busy: boolean;
  onSetOverride: (amount: Money | null) => void;
}) {
  const [customAmount, setCustomAmount] = useState<string>(line?.extendedAmount ?? "");
  const [error, setError] = useState<string | null>(null);
  const confirmGate = useConfirmGate();

  const currentAmount: Money = line?.extendedAmount ?? ("0.00" as Money);
  // Informational only — never sent back to the server. The parts
  // subtotal that Shop Supplies is a percentage OF excludes the Shop
  // Supplies line itself (see PricingService.computeShopSupplies()'s
  // "parts_subtotal" basis).
  const basisAmount = Number(partsTotal) - Number(currentAmount);
  const currentPercent = basisAmount > 0 ? ((Number(currentAmount) / basisAmount) * 100).toFixed(2) : "0.00";
  const ratePercent = (Number(rateSnapshot) * 100).toFixed(2);
  const isRemoved = overrideAmount === "0.00";
  const isOverridden = overrideAmount != null;

  function applyCustomAmount(): void {
    const parsed = parseMoneyInput(customAmount);
    if (!parsed) {
      setError('Amount should look like "17.91".');
      return;
    }
    // Informational, early feedback only — the server re-checks this
    // against tenant_settings.shop_supplies_cap and is the real enforcement
    // point (see WorkOrdersService.update()).
    if (cap != null && Number(parsed) > Number(cap)) {
      setError(`Amount can't exceed the shop's cap of ${formatMoney(cap)}.`);
      return;
    }
    setError(null);
    confirmGate.request(() => onSetOverride(parsed));
  }

  if (!line && !isOverridden) return null;

  return (
    <div className="mt-3.5 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-[#101b2d]">Shop Supplies</span>
          <ModernInfoTip text={`Automatically calculated as ${ratePercent}% of the parts subtotal, unless you set a custom amount below.`} />
        </div>
        <span className="text-[14px] font-semibold text-[#071a33]">{isRemoved ? "Removed" : formatMoney(currentAmount)}</span>
      </div>

      {error && <ModernErrorBanner message={error} />}

      {!isRemoved && (
        <p className="m-0 mb-3 text-[13px] text-[#6b7a8f]">
          {isOverridden ? "Custom amount" : "Automatic amount"} — currently {currentPercent}% of the parts subtotal.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <ModernField label="Custom Amount" htmlFor="m-shopSuppliesAmount" className="!mb-0 w-32 flex-none">
          <ModernInput
            id="m-shopSuppliesAmount"
            inputMode="decimal"
            placeholder="0.00"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
          />
        </ModernField>
        <ModernButton type="button" variant="secondary" disabled={busy} onClick={applyCustomAmount}>
          Set Custom Amount
        </ModernButton>
        {!isRemoved && (
          <ModernButton
            type="button"
            variant="danger"
            disabled={busy}
            onClick={() => confirmGate.request(() => onSetOverride("0.00" as Money))}
          >
            Remove Shop Supplies
          </ModernButton>
        )}
        {isOverridden && (
          <ModernButton type="button" variant="secondary" disabled={busy} onClick={() => confirmGate.request(() => onSetOverride(null))}>
            Use Automatic Calculation
          </ModernButton>
        )}
      </div>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Update how Shop Supplies is calculated for this work order?" />
    </div>
  );
}

/** Work-order discount entry — % of the pre-tax subtotal or a flat $
 * amount (see PricingService). The server clamps and validates; this just
 * collects type + value and sends both, or both null to clear. */
function ModernDiscountRow({
  type,
  value,
  disabled,
  onSet,
}: {
  type: "percent" | "amount" | null;
  value: Money | null;
  disabled: boolean;
  onSet: (values: { discountType: "percent" | "amount" | null; discountValue: Money | null }) => void;
}) {
  const [draftType, setDraftType] = useState<"percent" | "amount">(type ?? "percent");
  const [draftValue, setDraftValue] = useState(value ?? "");
  const parsed = draftValue.trim() === "" ? null : Number(draftValue);
  const valid = parsed !== null && Number.isFinite(parsed) && parsed > 0 && (draftType !== "percent" || parsed <= 100);
  return (
    <div>
      <div className="mb-1.5 text-[13px] font-bold text-[#071a33]">Discount</div>
      <div className="flex flex-wrap items-center gap-2">
        <ModernSelect
          aria-label="Discount type"
          className="!w-[72px]"
          value={draftType}
          disabled={disabled}
          onChange={(e) => setDraftType(e.target.value as "percent" | "amount")}
        >
          <option value="percent">%</option>
          <option value="amount">$</option>
        </ModernSelect>
        <ModernInput
          aria-label="Discount value"
          inputMode="decimal"
          className="!w-[110px]"
          placeholder={draftType === "percent" ? "10" : "25.00"}
          value={draftValue}
          disabled={disabled}
          onChange={(e) => setDraftValue(e.target.value)}
        />
        <ModernButton
          type="button"
          variant="secondary"
          disabled={disabled || !valid}
          onClick={() => onSet({ discountType: draftType, discountValue: Number(draftValue).toFixed(2) as Money })}
        >
          Apply
        </ModernButton>
        {type !== null && (
          <ModernButton
            type="button"
            variant="secondary"
            disabled={disabled}
            onClick={() => {
              setDraftValue("");
              onSet({ discountType: null, discountValue: null });
            }}
          >
            Remove
          </ModernButton>
        )}
      </div>
      <p className="mb-0 mt-1.5 text-[12px] text-[#8b97a8]">Taken off before tax.</p>
    </div>
  );
}
